import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';

import IconButton from 'components/common/IconButton';
import Badge from 'components/common/Badge';
import PatientDeleteDialog from 'components/patient/PatientDeleteDialog';
import EventLogViewDialog from 'components/patient/EventLogViewDialog';
import Menu, { MenuItem } from 'components/common/Menu';
import IconMoreVert from 'components/icons/IconMoreVert';
import Chip from 'components/common/Chip';
import Link from 'components/common/Link';
import Tooltip from 'components/common/Tooltip';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import IconWatch from 'components/icons/IconWatch';
import Watchers from 'components/patient/Watchers';
import IconRural from 'components/icons/IconRural';

import selectInsurance from 'utils/selectInsurance';

import patientDobToAge from 'utils/patientDobToAge';
import splitLongWords from 'utils/splitLongWords';
import getPatientFullAddress from 'utils/print/getPatientFullAddress';

import PatientsTableRowInsuranceInformation from './PatientsTableRowInsuranceInformation';

const styles = theme => ({
  notification: {
    display: 'inline-block',
    width: 10,
    height: 10,
    backgroundColor: theme.palette.notification['500'],
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  buttonCell: {
    padding: '0 10px 0 0',
    '&:last-child': {
      padding: '0 10px 0 0',
    },
  },
  chip: {
    verticalAlign: 'middle',
    marginLeft: 8,
  },
  cellPhone: {
    whiteSpace: 'nowrap',
  },
  statusIcon: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
    cursor: 'pointer',
  },
  statusIconActive: {
    backgroundColor: theme.palette.active[500],
  },
  statusIconInactive: {
    backgroundColor: theme.palette.validation[500],
  },
  cellName: {
    position: 'relative',
    '&:first-child': {
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 24,
      },
    },
  },
  cellWatcher: {
    padding: 0,
  },
  tooltipStatus: {
    position: 'absolute',
    top: '50%',
    left: 8,
    marginTop: -8,
  },
  iconWatch: {
    width: 24,
    height: 24,
  },
  iconButton: {
    margin: -4,
    color: theme.palette.grey[600],
  },
  iconButtonActive: {
    color: theme.palette.primary[500],
  },
  typographyCount: {
    width: 15,
  },
  typographyCountActive: {
    color: theme.palette.primary[500],
  },
  tooltipRural: {
    verticalAlign: 'middle',
  },
  iconRural: {
    width: 24,
    height: 24,
    marginRight: 8,
    marginTop: -6,
    color: theme.palette.primary[500],
  },
  drawerPaper: {
    padding: 16,
  },
  gridContainer: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
});

class PatientsTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteDilogOpen: false,
      eventLogViewDialogOpen: false,
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
      watcherMenuOpen: false,
      patientMenuOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteDialogClose = this.onDeleteDialogClose.bind(this);
    this.onEventLogViewItemClick = this.onEventLogViewItemClick.bind(this);
    this.onEventLogViewDialogClose = this.onEventLogViewDialogClose.bind(this);
    this.onPrintItemClick = this.onPrintItemClick.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onWatchButtonClick = this.onWatchButtonClick.bind(this);
  }

  onMenuButtonClick(event) {
    event.stopPropagation();
    this.setState({
      patientMenuOpen: true,
      watcherMenuOpen: false,
      anchorEl: event.currentTarget,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onDeleteMenuItemClick() {
    this.setState({
      deleteDilogOpen: true,
      anchorEl: null,
    });
  }

  onDeleteDialogClose() {
    this.setState({ deleteDilogOpen: false });
  }

  onEventLogViewItemClick() {
    this.setState({
      eventLogViewDialogOpen: true,
      anchorEl: null,
    });
  }

  onEventLogViewDialogClose() {
    this.setState({ eventLogViewDialogOpen: false });
  }

  onPrintItemClick() {
    this.setState({
      printDialogOpen: true,
      anchorEl: null,
    });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onWatchButtonClick(event) {
    this.setState({
      patientMenuOpen: false,
      watcherMenuOpen: true,
      anchorEl: event.currentTarget,
    });
  }

  render() {
    const { patient, hover, menu, onClick, account, business, width, classes } = this.props;
    const {
      anchorEl,
      deleteDilogOpen,
      eventLogViewDialogOpen,
      printDialogOpen,
      signDialogOpen,
      printFormValues,
      watcherMenuOpen,
      patientMenuOpen,
    } = this.state;
    const insurances = patient['Insurance Information'].filter(item =>
      item['Insurance Type'] !== 'Self');
    const watchedByUser = patient.Watcher.filter(item => item.ID === account.ID).length > 0;
    return (
      <Fragment>
        <TableRow
          hover={hover}
          {...onClick ? {
            onClick,
            onKeyDown: (event) => {
              if (event.key === 'Enter') {
                onClick();
              }
            },
            tabIndex: 0,
          } : {}}
        >
          <TableCell className={classes.cellName}>
            <Tooltip
              className={classes.tooltipStatus}
              content={
                <Typography>
                  {patient['Patient Status'] === true && 'Active'}
                  {(patient['Patient Status'] === false && patient['Date of Death']) && `Inactive, DOD: ${patient['Date of Death']}`}
                  {(patient['Patient Status'] === false && !patient['Date of Death']) && 'Inactive'}
                </Typography>
              }
              placement="top"
            >
              <div
                className={classNames(classes.statusIcon, {
                  [classes.statusIconActive]: patient['Patient Status'] === true,
                  [classes.statusIconInactive]: patient['Patient Status'] === false,
                })}
              />
            </Tooltip>
            {onClick ? (
              patient['Last Name']
            ) : (
              <Link
                href={`/charts/${patient['Record Number']}`}
                breakWord
              >
                {splitLongWords(patient['Last Name'])}
              </Link>
            )}
          </TableCell>
          <TableCell>
            {onClick ? (
              patient['First Name']
            ) : (
              <Link
                href={`/charts/${patient['Record Number']}`}
                breakWord
              >
                {patient['First Name']}
              </Link>
            )}
          </TableCell>
          <TableCell>
            {patient['Date of Birth']}
            {!patient['Date of Death'] &&
            `, ${patientDobToAge(patient['Date of Birth'])}`}
          </TableCell>
          <TableCell>
            <Badge
              color="primary"
              tooltipText={patient.Gender}
            >
              {patient.Gender.slice(0, 1)}
            </Badge>
          </TableCell>
          <TableCell>
            {patient['Billing Address Information']['Is Rural'] &&
              <Tooltip
                className={classes.tooltipRural}
                content={
                  <Typography>
                    Rural Billing Address
                  </Typography>
                }
                placement="top"
              >
                <IconRural className={classes.iconRural} />
              </Tooltip>
            }
            {getPatientFullAddress({ patient, billing: true })}
          </TableCell>
          <TableCell className={classes.cellPhone}>
            {patient['Contact Information']['Primary Phone']}
          </TableCell>
          <TableCell>
            {/* TBD Order Benefits needed for insurance filtering */}
            {insurances.length > 0 &&
              selectInsurance(insurances)['Insurance Info']['Payer Name']
            }
            {insurances.length > 1 &&
              <Tooltip
                content={
                  <PatientsTableRowInsuranceInformation
                    {...{ 'Insurance Information': insurances }}
                  />
                }
                placement="top"
              >
                <Chip
                  className={classes.chip}
                  color="primary"
                  borderColor="primary"
                  label={`+${insurances.length - 1}`}
                />
              </Tooltip>
            }
          </TableCell>
          <TableCell>
            {/* TBD Tooltip with referal information */}
            {patient['Sales Representative'].length > 0 &&
              splitLongWords(`${patient['Sales Representative'][0]['First Name']} ${patient['Sales Representative'][0]['Last Name']}`)
            }
          </TableCell>
          {menu &&
            <TableCell className={classes.cellWatcher}>
              <IconButton
                className={classNames(classes.iconButton, {
                  [classes.iconButtonActive]: watchedByUser,
                })}
                aria-label="More"
                aria-owns={watcherMenuOpen && Boolean(anchorEl) ? `${patient['Record Number']}-watcher-menu` : null}
                aria-haspopup="true"
                onClick={this.onWatchButtonClick}
                tooltipContent="Watching"
              >
                <IconWatch className={classes.iconWatch} />
                <Typography
                  variant="caption"
                  className={classNames(classes.typographyCount, {
                    [classes.typographyCountActive]: watchedByUser,
                  })}
                >
                  {patient.Watcher.length > 0 ? patient.Watcher.length : ''}
                </Typography>
              </IconButton>
              <Menu
                id={`${patient['Record Number']}-watcher-menu`}
                anchorEl={anchorEl}
                open={watcherMenuOpen && Boolean(anchorEl)}
                onClose={this.onMenuClose}
              >
                <Watchers
                  patient={patient}
                  onClose={this.onMenuClose}
                />
              </Menu>
            </TableCell>
          }
          {/* TBD Notifications needed
            <TableCell padding="none">
                {notification &&
                <span className={classes.notification}>Notification</span>
            </TableCell>
          } */}
          {menu &&
            <TableCell className={classes.buttonCell} numeric>
              <IconButton
                aria-label="More"
                aria-owns={patientMenuOpen && Boolean(anchorEl) ? `${patient['Record Number']}-patient-menu` : null}
                aria-haspopup="true"
                onClick={this.onMenuButtonClick}
                tooltipContent="Open Menu"
              >
                <IconMoreVert
                  className={classes.iconMoreVert}
                />
              </IconButton>
              <Menu
                id={`${patient['Record Number']}-patient-menu`}
                anchorEl={anchorEl}
                open={patientMenuOpen && Boolean(anchorEl)}
                onClose={this.onMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    window.open(`/workflow/order/create/patient/${patient['Record Number']}`, '_blank');
                    this.onMenuClose();
                  }}
                >
                  Create Order
                </MenuItem>
                <MenuItem onClick={this.onPrintItemClick}>
                  Print Data
                </MenuItem>
                <MenuItem onClick={this.onEventLogViewItemClick}>
                  View Activity
                </MenuItem>
                <MenuItem
                  onClick={this.onDeleteMenuItemClick}
                  color="warning"
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Menu>
              <PatientDeleteDialog
                open={deleteDilogOpen}
                onClose={this.onDeleteDialogClose}
                {...{ 'Record Number': patient['Record Number'] }}
                {...{ 'First Name': patient['First Name'] }}
                {...{ 'Middle Name': patient['Middle Name'] }}
                {...{ 'Last Name': patient['Last Name'] }}
              />
              <EventLogViewDialog
                open={eventLogViewDialogOpen}
                onClose={this.onEventLogViewDialogClose}
                {...{ 'Record Number': patient['Record Number'] }}
                business={business}
              />
              <PrintDialog
                form={`${patient['Record Number']}-patientPrint`}
                open={printDialogOpen}
                onClose={this.onPrintDialogClose}
                onSign={this.onPrintDialogSign}
                patient={patient}
              />
              <DocumentsSignDialog
                open={signDialogOpen}
                onClose={this.onSignDialogClose}
                printFormValues={printFormValues}
                patient={patient}
                variant="patient"
              />
            </TableCell>
          }
        </TableRow>
      </Fragment>
    );
  }
}

PatientsTableRow.propTypes = {
  patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Middle Name': PropTypes.string,
    'Record Number': PropTypes.number.isRequired,
    'Date of Birth': PropTypes.string.isRequired,
    Gender: PropTypes.string.isRequired,
    'Referral Source': PropTypes.arrayOf(PropTypes.shape({
      'Name of source': PropTypes.string,
    })).isRequired,
    'Patient Status': PropTypes.bool.isRequired,
    'Address Information': PropTypes.shape({
      Apt: PropTypes.string,
      Street: PropTypes.string,
    }).isRequired,
    'Contact Information': PropTypes.shape({
      'Primary Phone': PropTypes.string,
    }).isRequired,
    'Insurance Information': PropTypes.arrayOf(PropTypes.shape({
      'Insurance Status': PropTypes.string,
      'Insurance Info': PropTypes.shape({
        'Payer Name': PropTypes.string,
      }),
    })),
  }).isRequired,
  hover: PropTypes.bool,
  menu: PropTypes.bool,
  onClick: PropTypes.func,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    notification: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
    buttonCell: PropTypes.string.isRequired,
    cellPhone: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
    statusIcon: PropTypes.string.isRequired,
    statusIconActive: PropTypes.string.isRequired,
    statusIconInactive: PropTypes.string.isRequired,
  }).isRequired,
};

PatientsTableRow.defaultProps = {
  hover: false,
  menu: true,
  onClick: null,
  business: {},
};

const mapStateToProps = store => ({
  account: store.account,
});

export default connect(mapStateToProps)(
  withRouter(withWidth()(withStyles(styles)(PatientsTableRow))),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientsTable/PatientsTableRow.js